import { createFileRoute, useNavigate } from "@tanstack/react-router";
export const Route = createFileRoute("/admin/_layout/expense/reports")({
  component: AdminExpenseReportsPage,
});

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { ExpenseReportTable } from "@/components/expense/tables/ExpenseReportTable";
import { ExpenseReportCreateSheet } from "@/components/expense/forms/ExpenseReportCreateSheet";
import { useDebounce } from "@/hooks/use-debounce";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import {
  useFetchAdminExpenseReports,
  useExpenseReportMutations,
  type ExpenseReportOrdering,
  type ExpenseReportOrderingField,
} from "@/hooks/api/expense/useExpenseReports";
import type { ExpenseReport, ExpenseStatus } from "@/types/expense";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";
import { useAuthStore } from "@/stores/useAuthStore";

const STATUS_OPTIONS = [
  { label: "Submitted", value: "SUBMITTED" },
  { label: "Approved", value: "APPROVED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Cancelled", value: "CANCELLED" },
  { label: "Paid", value: "PAID" },
] satisfies { label: string; value: ExpenseStatus }[];

function AdminExpenseReportsPage() {
  const navigate = useNavigate();
  const { data: capabilities } = useMyPermissions();
  // Same code gates both: filing a report on someone else's behalf, and
  // acting as "manage" for the owner-or-manage gate on Cancel below.
  const canFileOnBehalf = can(capabilities, "expense.report.manage");
  const canView = can(capabilities, "expense.report.view");
  const authUser = useAuthStore((s) => s.user);

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ExpenseStatus | undefined>(
    undefined
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebounce(searchQuery, 500);
  const [cancelTarget, setCancelTarget] = useState<ExpenseReport | null>(null);
  const [createSheetOpen, setCreateSheetOpen] = useState(false);

  const { ordering, toggleOrdering, getApiOrdering } = useDataTableOrdering<
    {
      title: SortDirection;
      period_start: SortDirection;
      total_amount: SortDirection;
      created_at: SortDirection;
    },
    ExpenseReportOrdering
  >({ title: null, period_start: null, total_amount: null, created_at: null });

  const reportsQuery = useFetchAdminExpenseReports(
    {
      page_size: pageSize,
      page,
      ordering: getApiOrdering(),
      search: debouncedSearch || undefined,
      status: statusFilter,
    },
    { enabled: canView },
  );
  const reports = reportsQuery.data?.results ?? [];
  const count = reportsQuery.data?.count ?? 0;

  const { cancelReport, submitReport } = useExpenseReportMutations();

  const isMutating = cancelReport.isPending || submitReport.isPending;
  const isLoading = (canView && reportsQuery.isPending) || isMutating;
  const isFetching = (canView && reportsQuery.isFetching) || isMutating;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, statusFilter, setPage]);

  const handleOrderingChange = (field: ExpenseReportOrderingField) => {
    toggleOrdering(field);
  };

  const handleSubmit = async (report: ExpenseReport) => {
    try {
      await submitReport.mutateAsync({ id: report.id });
      toast.success("Report submitted");
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    try {
      await cancelReport.mutateAsync({ id: cancelTarget.id });
      toast.success("Report cancelled");
      setCancelTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Expense Reports"
          description="Review and manage all employee expense submissions."
          onToggleFilter={() => setShowFilters((p) => !p)}
          onAdd={() => setCreateSheetOpen(true)}
          addLabel="New Report"
          isLoading={isFetching}
        />

        {canView && (
          <>
            <FiltersPanel
              show={showFilters}
              onClear={() => {
                setStatusFilter(undefined);
                setSearchQuery("");
              }}
              isLoading={isFetching}
            >
              <SearchFilter
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search reports..."
              />
              <SelectFilter
                placeholder="Status"
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={setStatusFilter}
              />
            </FiltersPanel>

            <ExpenseReportTable
              reports={reports}
              isLoading={isLoading}
              isAdmin
              ordering={ordering}
              onOrderingChange={handleOrderingChange}
              onView={(r) =>
                navigate({
                  to: "/admin/expense/reports/$id",
                  params: { id: String(r.id) },
                })
              }
              onReview={(r) =>
                navigate({
                  to: "/admin/expense/reports/$id",
                  params: { id: String(r.id) },
                })
              }
              onSubmit={handleSubmit}
              onCancel={(r) => setCancelTarget(r)}
              canManage={canFileOnBehalf}
              currentUserId={authUser?.user_id}
            />

            <PaginationComponent
              count={count}
              page={page}
              onPageChange={setPage}
              isLoading={isFetching}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </>
        )}
      </div>

      <ExpenseReportCreateSheet
        adminMode={canFileOnBehalf}
        isOpen={createSheetOpen}
        onClose={() => setCreateSheetOpen(false)}
      />

      <DeleteConfirmDialog
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancelConfirm}
        isLoading={cancelReport.isPending}
        title="Cancel Report"
        description={`Cancel "${cancelTarget?.title}"? This cannot be undone.`}
      />
    </SidebarInset>
  );
}
